<script setup>
import { computed } from 'vue';
  import { useMapUIStore } from '@/stores/MapUIStore';
  import { useStormStore } from "@/stores/StormStore";
  import { useMapStore } from "@/stores/MapStore";
  import { useProductStore } from "@/stores/ProductStore";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import MapSidebarPinnedMaps from "@/components/MapSidebarPinnedMaps.vue";
  import MapButtonSidebar from "@/components/MapButtonSidebar.vue";
  import Icon from '@/components/icons/Icon.vue';
  import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals';

  const analyticInfo = {
    'map-selection': {
      event: 'Show_Map_Regional_Products',
      parameters: {'Initiator': 'Map_Button'}
    },
    'storm-selection': {
      event: 'Show_Map_Storm_Selection'
    },
    'layers-dashboard': {
      event: 'Show_Map_Layers_Dashboard'
    },
    'favorites': {
      event: 'Show_Map_Favorites'
    },
    'map-custom-layers': {
      'event': 'Show_Map_Custom_Layers'
    },
    'customization': {
      event: 'Show_Map_Customization'
    },
    'storm-print-mesonet': {
      event: 'Show_Map_Mesonet_Selection'
    },
    'settings': {
      event: 'Show_Map_Settings'
    }
  }

  const {
    $PINNED_MAP_BAR,
    $MAP_CUSTOM_LAYERS,
    $MAP_SELECTOR_BUTTON,
    $FAVORITES_BUTTON,
    $CUSTOMIZE_MAP_BUTTON, 
    $MESONET_FILTER_BUTTON,
    $STORM_MAP_SELECTOR_BUTTON,
  } = useMapConfigurationGlobals();

  const mapUIStore = useMapUIStore();
  const stormStore = useStormStore();
  const mapStore = useMapStore();
  const productStore = useProductStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const showLayersDashboardButton = computed(() => !isDesktop.value && (mapStore.state.overlays['watches-warnings'] || mapStore.state.overlays['dsat-operating-condition'] || mapStore.state.overlays['lightning']));
  
  function handleClick(content) {
    const contentAnalyticsInfo = analyticInfo[content];

    if (!contentAnalyticsInfo) return;

    const { event } = contentAnalyticsInfo;
    const parameters = contentAnalyticsInfo.parameters || null;

    googleAnalyticsStore.sendEvent(event, parameters);    
  }

</script>

<template>
  <div class="jwx-sidebar" data-map="sidebar">

    <div class="jwx-sidebar__primary">
      <div 
        v-if="$MAP_SELECTOR_BUTTON"
        class="jwx-wrapper-block"
      >
        <MapButtonSidebar 
          @mapButton:click="handleClick"
          class="jwx-button-control--primary" 
          type="config" 
          content="map-selection"
          accessibleLabel="Map Selector"
        >
          <Icon name="map" class="jwx-button-control__icon" />  
        </MapButtonSidebar>
      </div>
      <div
        v-if="$STORM_MAP_SELECTOR_BUTTON"
        class="jwx-wrapper-block"
      >
        <MapButtonSidebar
          @mapButton:click="handleClick"
          class="jwx-button-control--primary" 
          type="config" 
          content="storm-selection"
          accessibleLabel="Storm Selection"
        >
          <Icon name="hurricane" class="jwx-button-control__icon" />
          <span v-if="stormStore.activeStorms.length > 0" class="jwx-button-control__badge-count">{{ stormStore.activeStorms.length }}</span>
        </MapButtonSidebar>
      </div>
    </div>

    <Suspense>
      <MapSidebarPinnedMaps v-if="$PINNED_MAP_BAR" />
    </Suspense>

    <div v-if="showLayersDashboardButton" class="jwx-sidebar__secondary jwx-wrapper-block jwx-wrapper-block--transparent-border jwx-wrapper-block--column">
      <ul class="jwx-wrapper-block__content jwx-wrapper-block__items">
        <li class="jwx-wrapper-block__item">
          <MapButtonSidebar
            @mapButton:click="handleClick"
            type="config"
            content="layers-dashboard"
            accessibleLabel="Layers Dashboard"
          >
          <Icon name="dashboard" class="jwx-button-control__icon" />  
          </MapButtonSidebar>
        </li>    
        </ul> 
    </div>

    <div class="jwx-sidebar__secondary jwx-wrapper-block jwx-wrapper-block--transparent-border jwx-wrapper-block--column">
      <ul class="jwx-wrapper-block__content jwx-wrapper-block__items" :data-reverse="!isDesktop || null">
        <li class="jwx-wrapper-block__item" v-if="$FAVORITES_BUTTON">
          <MapButtonSidebar
          @mapButton:click="handleClick"
          type="config"
          content="favorites"
          accessibleLabel="Favorites List"
        >
          <Icon name="favorites-folder" class="jwx-button-control__icon" />  
          </MapButtonSidebar>
        </li>
        <li class="jwx-wrapper-block__item" v-if="$MAP_CUSTOM_LAYERS && productStore.customLayers.length > 0">
          <MapButtonSidebar
          @mapButton:click="handleClick"
          type="config"
          content="map-custom-layers"
          accessibleLabel="Map Custom Layers"
        >
          <Icon name="custom-layers" class="jwx-button-control__icon" />  
          </MapButtonSidebar>
        </li>   
        <li v-if="mapUIStore.showCustomizeMapButton && $CUSTOMIZE_MAP_BUTTON" class="jwx-wrapper-block__item">
          <MapButtonSidebar
            @mapButton:click="handleClick"
            type="config"
            content="customization"
            accessibleLabel="Customize Map"
          >
            <Icon name="sliders" class="jwx-button-control__icon" />  
          </MapButtonSidebar>
        </li>   
        <li v-if="mapUIStore.showSpotFilterButton && $MESONET_FILTER_BUTTON" class="jwx-wrapper-block__item">
          <MapButtonSidebar
            @mapButton:click="handleClick"
            type="config"
            content="storm-print-mesonet"
            accessibleLabel="Mesonet Filter"
          >
            <Icon name="filter" class="jwx-button-control__icon" />  
          </MapButtonSidebar>
        </li>              
        <li class="jwx-wrapper-block__item">
          <MapButtonSidebar
            @mapButton:click="handleClick"
            type="config"
            content="settings"
            accessibleLabel="Map Settings"
          >
            <Icon name="gear" class="jwx-button-control__icon" />  
          </MapButtonSidebar>
        </li>        
      </ul>
    </div>

  </div>
</template>